import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TYPE_LABELS = {
  pregnant_woman: 'Pregnant Women',
  lactating_mother: 'Lactating Mothers',
  children_0m_6m: 'Children 0-6m',
  children_6m_3y: 'Children 6m-3y',
  children_3y_6y: 'Children 3-6y',
};

export default function DropoffBeneficiaryModal({
  isOpen,
  onClose,
  beneficiaryType,
  title,
  description,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const label = useMemo(() => TYPE_LABELS[beneficiaryType] || beneficiaryType, [beneficiaryType]);

  useEffect(() => {
    if (!isOpen) return;
    setQ('');
    setItems([]);
    setTotal(0);
    setPage(0);
    setError(null);
  }, [isOpen, beneficiaryType]);

  useEffect(() => {
    if (!isOpen) return;
    if (!beneficiaryType) return;

    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/api/beneficiary-report/dropoffs/beneficiaries`, {
          params: {
            beneficiary_type: beneficiaryType,
            q: q || undefined,
            offset: page * pageSize,
            limit: pageSize,
          },
          signal: controller.signal,
        });
        setItems(res.data?.items || []);
        setTotal(res.data?.total || 0);
      } catch (e) {
        if (e?.name === 'CanceledError') return;
        console.error('Error fetching drop-off beneficiaries:', e);
        setError('Failed to load beneficiary list. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    return () => controller.abort();
  }, [isOpen, beneficiaryType, q, page]);

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{title || `${label} — Drop-off Drilldown`}</DialogTitle>
          <DialogDescription>
            {description || 'Beneficiaries considered “drop-off” in this dashboard when monitoring is not happening (is_monitored=false).'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-orange-600">{label}</Badge>
            <span className="text-xs text-gray-500">
              {total.toLocaleString()} beneficiaries (is_monitored=false)
            </span>
          </div>

          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search beneficiary name..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
          </div>
        </div>

        {error && (
          <div className="p-3 rounded border border-red-200 bg-red-50 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>AWC</TableHead>
                <TableHead className="text-center">Age (months)</TableHead>
                <TableHead className="text-center">Risk</TableHead>
                <TableHead className="text-center">Priority</TableHead>
                <TableHead className="text-center">Contactable</TableHead>
                <TableHead className="text-center">Mobile</TableHead>
                <TableHead className="text-center">Aadhaar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <div className="flex items-center justify-center py-8 text-gray-600">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Loading beneficiaries...
                    </div>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <div className="py-8 text-center text-gray-500">
                      No beneficiaries found.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-medium">{b.beneficiary_name || 'N/A'}</TableCell>
                    <TableCell>{b.district || 'N/A'}</TableCell>
                    <TableCell>{b.sector || 'N/A'}</TableCell>
                    <TableCell className="text-xs">
                      {b.awc_name || 'N/A'} {b.awc_code ? `(${b.awc_code})` : ''}
                    </TableCell>
                    <TableCell className="text-center">{b.age_months ?? '—'}</TableCell>
                    <TableCell className="text-center">
                      <span className={b.drop_off_risk === 'high' ? 'text-red-700 font-semibold' : 'text-gray-600'}>
                        {b.drop_off_risk || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">{b.reengagement_priority ?? '—'}</TableCell>
                    <TableCell className="text-center">{b.contactable ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">{b.mobile_verified ? '✓' : '✗'}</TableCell>
                    <TableCell className="text-center">{b.aadhaar_verified ? '✓' : '✗'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">
            Page {page + 1} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={!canPrev || loading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              disabled={!canNext || loading}
            >
              Next
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


